/**
 * Hook to configure and manage WebSocket connection for SAAGA Agents API.
 * Handles authentication and connection lifecycle for chat routes.
 */

import { useAuth, useUser } from '@clerk/clerk-react'
import {
  type UseAgentsWebsocketReturn,
  useAgentsWebsocket,
} from '@saaga/shared/hooks/use-agents-websocket.hook'
import { getPlan } from '@saaga/shared/store/token-provider.store'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useWebsocketAutoReconnect } from './use-websocket-auto-reconnect.hook'

const AGENTS_WS_URL = import.meta.env.VITE_AGENTS_WS_URL ?? ''

export interface UseAgentsWebsocketConfigReturn extends UseAgentsWebsocketReturn {
  initializeConnection: () => Promise<void>
}

export const useAgentsWebsocketConfig = (): UseAgentsWebsocketConfigReturn => {
  const { getToken, orgId } = useAuth()
  const { user } = useUser()
  const websocket = useAgentsWebsocket()
  const isInitializingRef = useRef(false)
  const disconnectRef = useRef(websocket.disconnect)
  disconnectRef.current = websocket.disconnect
  const prevOrgIdRef = useRef(orgId)

  const initializeConnection = useCallback(async () => {
    if (isInitializingRef.current || websocket.isConnected || websocket.isConnecting) return
    if (!AGENTS_WS_URL) return

    isInitializingRef.current = true
    try {
      const token = await getToken()
      if (token) {
        websocket.connect({
          url: AGENTS_WS_URL,
          token,
          tenantId: orgId ?? undefined,
          userId: user?.id,
          plan: getPlan() ?? undefined,
        })
      }
    } finally {
      isInitializingRef.current = false
    }
  }, [getToken, orgId, user?.id, websocket])

  useEffect(() => {
    if (prevOrgIdRef.current === orgId) return
    const hadPreviousOrg = prevOrgIdRef.current !== null
    prevOrgIdRef.current = orgId
    if (!hadPreviousOrg) return

    disconnectRef.current()
    isInitializingRef.current = false
  }, [orgId])

  useWebsocketAutoReconnect({ initializeConnection })

  useEffect(() => {
    return () => {
      disconnectRef.current()
    }
  }, [])

  return useMemo(
    () => ({
      ...websocket,
      initializeConnection,
    }),
    [websocket, initializeConnection]
  )
}
